import { pack } from '@attain-apps/data-utils'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type { Collection } from '../../../collection'
import type * as ecModels from '../../../models'

import type { Plugin } from './plugin'
import type { State } from './state'

/**
 * Core actions for this {@link Plugin}
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig<
  TTypeParams extends ecModels.TypeParams.Constraint,
>(collection: Collection<TTypeParams>) {
  return {
    /* eslint-disable sort-keys */
    /**
     * Retrieves the number of entities that matches the given request
     */
    count: store.actions.request({
      input: (
        request: Pick<ecModels.QueryRequest<TTypeParams>, 'filter'> = {},
      ) => request,
      output: store.actions.Data.asIs<number>(),
    }),
    /**
     * Retrieves the requested entity, if it exists
     *
     * If it does not exist, this will return `undefined`
     */
    get: store.actions.request({
      input: store.actions.Data.asIs<{
        readonly id: ecModels.EntityId
      }>(),
      output: store.actions.Data.asIs<{
        // NOTE: we just return `undefined` when the entity does not exist
        readonly entity?: TTypeParams['entity']
      }>(),
      reducersDeprecated: {
        error: (state, { error, request: { id } }) => {
          state = {
            ...state,
            statuses: { ...state.statuses },
          }
          state.statuses[id] = { error }
          return state
        },
        request: (state, { id }) => {
          state = {
            ...state,
            statuses: { ...state.statuses },
          }
          state.statuses[id] = { isLoading: true }
          return state
        },
        success: (state, { request: { id } }) => {
          state = {
            ...state,
            statuses: { ...state.statuses },
          }
          // No longer loading nor error
          delete state.statuses[id]
          return state
        },
      },
    }),
    /**
     * Retrieves the requested entities
     */
    list: store.actions.request({
      input: (
        options: ecModels.QueryRequest<TTypeParams> & {
          readonly ids?: readonly ecModels.EntityId[]
        } = {},
      ) => options as FlattenUnion<typeof options>,
      output: store.actions.Data.asIs<{
        readonly entities: readonly TTypeParams['entity'][]
      }>(),
      reducersDeprecated: {
        error: (state, { error, request: { ids } }) => {
          if (!ids) {
            // If there are no ids, we can't mark the status of any particular entities
            return state
          }
          state = {
            ...state,
            statuses: { ...state.statuses },
          }
          for (const id of ids) {
            state.statuses[id] = { error }
          }
          return state
        },
        request: (state, { ids }) => {
          if (!ids) {
            // If there are no ids, we can't mark the status of any particular entities
            return state
          }
          state = {
            ...state,
            statuses: { ...state.statuses },
          }
          for (const id of ids) {
            state.statuses[id] = { isLoading: true }
          }
          return state
        },
        success: (state, { request: { ids }, response: { entities } }) => {
          state = {
            ...state,
            statuses: { ...state.statuses },
          }
          /**
           * IDs of all entities that were successfully retrieved
           */
          const successfulIds = entities.map(entity =>
            collection.identify(entity),
          )
          if (ids) {
            // If we requested these IDs, even if they were not returned, they
            // are no longer loading, so we need to include them
            successfulIds.push(...ids)
          }
          for (const id of successfulIds) {
            // No longer loading nor error
            delete state.statuses[id]
          }
          return state
        },
      },
    }),
    /**
     * Creates one or more entities
     */
    create: store.actions.request({
      input: ({
        input,
        inputs,
      }: ExactlyOneOf<{
        readonly input: TTypeParams['createEntity']
        readonly inputs: readonly TTypeParams['createEntity'][]
      }>): readonly TTypeParams['createEntity'][] => inputs ?? [input],
      output: store.actions.Data.asIs<
        // Will produce entities, if possible. Otherwise, undefined
        readonly TTypeParams['entity'][] | undefined
      >(),
    }),
    /**
     * Deletes one or more entities
     */
    delete: store.actions.request({
      input: ({
        id,
        ids,
      }: ExactlyOneOf<{
        readonly id: ecModels.EntityId
        readonly ids: readonly ecModels.EntityId[]
      }>) => ({ ids: ids ?? [id] }),
      output: store.actions.Data.NONE,
    }),
    /**
     * Updates one or more entities
     */
    update: store.actions.request({
      // TODO: we should probably change this to be consistent with the above, using ExactlyOneOf<{ update, updates }>
      input: (updates: Maybe.ReadonlyArray<ecModels.Api.Update<TTypeParams>>) =>
        pack(updates),
      output: store.actions.Data.asIs<
        // Will produce entities, if possible. Otherwise, undefined
        readonly TTypeParams['entity'][] | undefined
      >(),
    }),
    /* eslint-enable sort-keys */
  } as const satisfies store.actions.Config.Constraint<State<TTypeParams>>
}
